import React, { useState, useEffect, useCallback, useRef } from "react";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { InfoCircleIcon } from "../../assets/Icons/InfoCircleIcon";
import { LookupService } from "../../api/services";
import { useMsal } from "@azure/msal-react";
import debounce from "lodash/debounce";
import { MultiSelect } from "react-multi-select-component";
import { MotifSelect, MotifOption, MotifFormField } from "@ey-xd/motif-react";
import { Tooltip } from "../CommonUIComponents/Tooltip/Tooltip";
import { CustomTooltip } from "../CommonUIComponents/CustomTooltip/CustomTooltip";
import { USER_MANAGEMENT } from "../../util/constants";
import { CustomTooltipBottom } from "../CommonUIComponents/CustomTooltip/CustomTooltipBottom";

// Graph API endpoint for user search
const GRAPH_USERS_SEARCH_URL = "https://graph.microsoft.com/v1.0/users";

// Fetches user photo from Microsoft Graph API
const fetchUserPhoto = async (userId, accessToken) => {
  if (!userId || !accessToken) {
    return null;
  }

  try {
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    // Photo fetch is non-critical, just return null
    return null;
  }
};

// Fetches user suggestions from Microsoft Graph Users API
const fetchUserSuggestions = async (searchText, accessToken) => {
  if (!searchText || searchText.length < 2 || !accessToken) {
    return [];
  }

  try {
    const filter = `startswith(displayName,'${searchText}') or startswith(mail,'${searchText}') or startswith(userPrincipalName,'${searchText}')`;
    const response = await fetch(
      `${GRAPH_USERS_SEARCH_URL}?$filter=${encodeURIComponent(filter)}&$top=10&$select=id,displayName,mail`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      console.error("Graph API error:", response.status);
      return [];
    }

    const data = await response.json();
    const users = (data.value || [])
      .filter(
        (user) => user.mail && user.mail.toLowerCase().endsWith("gds.ey.com"),
      )
      .map((user) => ({
        id: user.id,
        displayName: user.displayName,
        mail: user.mail || "",
        photo: null, // Will be fetched separately
      }));

    // Fetch photos for all users in parallel
    const usersWithPhotos = await Promise.all(
      users.map(async (user) => {
        const photo = await fetchUserPhoto(user.id, accessToken);
        return { ...user, photo };
      }),
    );

    return usersWithPhotos;
  } catch (error) {
    console.error("Error fetching user suggestions:", error);
    return [];
  }
};

// Filter options for dropdowns
const filterOptions = {
  status: ["Active", "Inactive"],
};

// Super region guids are unique per region, but names can repeat across regions,
// so only fall back to a name match when the region also matches.
const isSameSuperRegion = (user, form) => {
  if (user.superRegionGuid && form.superRegionGuid) {
    return user.superRegionGuid === form.superRegionGuid;
  }
  return (
    user.superRegionName?.toLowerCase() === form.superRegion?.toLowerCase() &&
    (user.regionGuid === form.regionGuid ||
      user.regionName?.toLowerCase() === form.region?.toLowerCase())
  );
};

// User Modal component for adding/editing users
export const UserModal = ({
  isOpen,
  mode,
  userData,
  existingUsers = [],
  onClose,
  onSubmit,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    displayName: "",
    region: "",
    regionGuid: "",
    superRegion: "",
    superRegionGuid: "",
    role: "",
    roleGuid: "",
    countries: [], // Array of selected country objects
    status: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState(""); // Warning for duplicate detection on field change
  const [regions, setRegions] = useState([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [superRegions, setSuperRegions] = useState([]);
  const [isLoadingSuperRegions, setIsLoadingSuperRegions] = useState(false);
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [countries, setCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  // Email search state
  const [emailSearchText, setEmailSearchText] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [hasEmptySearchResults, setHasEmptySearchResults] = useState(false);
  const suggestionRef = useRef(null);

  // Acquire access token for Graph API
  useEffect(() => {
    if (inProgress === "none" && accounts.length > 0 && !accessToken) {
      const request = {
        scopes: ["User.Read", "User.ReadBasic.All"],
        account: accounts[0],
      };

      instance
        .acquireTokenSilent(request)
        .then((response) => {
          setAccessToken(response.accessToken);
        })
        .catch((error) => {
          console.error("Silent token acquisition failed:", error);
        });
    }
  }, [inProgress, accounts, instance, accessToken]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (searchText) => {
      if (searchText.length < 2) {
        setUserSuggestions([]);
        setShowSuggestions(false);
        setHasEmptySearchResults(false);
        return;
      }

      setIsLoadingSuggestions(true);
      const suggestions = await fetchUserSuggestions(searchText, accessToken);
      setUserSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setHasEmptySearchResults(suggestions.length === 0);
      setIsLoadingSuggestions(false);
    }, 300),
    [accessToken],
  );

  // Handle email search input change
  const handleEmailSearchChange = (e) => {
    const value = e.target.value;
    setEmailSearchText(value);
    setSelectedUser(null);
    setFormData((prev) => ({ ...prev, email: "" }));
    debouncedSearch(value);
  };

  // Handle user selection from suggestions
  const handleSelectUser = (user) => {
    const email = user.mail || "";
    const displayName = user.displayName || "";
    setSelectedUser(user);
    setFormData((prev) => ({ ...prev, email, displayName }));
    setEmailSearchText("");
    setShowSuggestions(false);
    setUserSuggestions([]);
    setHasEmptySearchResults(false);
    setSubmitError("");
    setDuplicateWarning("");
  };

  // Clear selected email
  const handleClearEmail = () => {
    setFormData((prev) => ({ ...prev, email: "", displayName: "" }));
    setSelectedUser(null);
    setEmailSearchText("");
    setSubmitError("");
    setDuplicateWarning("");
  };

  // Fetch regions when modal opens
  useEffect(() => {
    const fetchRegions = async () => {
      if (!isOpen) return;

      try {
        setIsLoadingRegions(true);
        const response = await LookupService.getRegions();
        setRegions(response?.data || response || []);
      } catch (error) {
        console.error("Error fetching regions:", error);
        setRegions([]);
      } finally {
        setIsLoadingRegions(false);
      }
    };

    fetchRegions();
  }, [isOpen]);

  // Fetch super regions when region changes
  useEffect(() => {
    const fetchSuperRegions = async () => {
      if (!formData.regionGuid) {
        setSuperRegions([]);
        return;
      }

      try {
        setIsLoadingSuperRegions(true);
        const response = await LookupService.getSuperRegionsByRegion(
          formData.regionGuid,
        );
        setSuperRegions(response?.data || response || []);
      } catch (error) {
        console.error("Error fetching super regions:", error);
        setSuperRegions([]);
      } finally {
        setIsLoadingSuperRegions(false);
      }
    };

    fetchSuperRegions();
  }, [formData.regionGuid]);

  // Fetch roles when region changes
  useEffect(() => {
    const fetchRoles = async () => {
      if (!formData.regionGuid) {
        setRoles([]);
        return;
      }

      try {
        setIsLoadingRoles(true);
        const response = await LookupService.getRolesByRegion(
          formData.regionGuid,
        );
        setRoles(response?.data || response || []);
      } catch (error) {
        console.error("Error fetching roles:", error);
        setRoles([]);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [formData.regionGuid]);

  // Fetch countries when superRegion changes
  useEffect(() => {
    const fetchCountries = async () => {
      if (!formData.superRegionGuid) {
        setCountries([]);
        return;
      }

      try {
        setIsLoadingCountries(true);
        const response = await LookupService.getCountriesBySuperRegion(
          formData.superRegionGuid,
        );
        setCountries(response?.data || response || []);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setCountries([]);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, [formData.superRegionGuid]);

  // Update form data when userData changes (for edit mode)
  useEffect(() => {
    if (userData && mode === "edit") {
      const email = userData.emailId || userData.email || "";

      // Parse countryNames and countryGuids/countryIds from comma-separated strings
      let parsedCountries = [];
      const countryNamesStr = userData.countryNames || "";
      const countryIdsStr = userData.countryGuids || userData.countryIds || "";

      if (countryNamesStr && countryIdsStr) {
        const names = countryNamesStr
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean);
        const ids = countryIdsStr
          .split(",")
          .map((g) => g.trim())
          .filter(Boolean);
        parsedCountries = names.map((name, index) => ({
          label: name,
          value: ids[index] ? parseInt(ids[index], 10) : name, // Convert to integer to match dropdown options
        }));
      } else if (countryNamesStr) {
        // If only names available, use names as both label and value
        const names = countryNamesStr
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean);
        parsedCountries = names.map((name) => ({
          label: name,
          value: name,
        }));
      } else if (userData.countries && Array.isArray(userData.countries)) {
        // If already in array format, use as is
        parsedCountries = userData.countries;
      }

      setFormData({
        userGuid: userData.userGuId || userData.userGuid || "",
        email: email,
        region: userData.regionName || userData.region || "",
        regionGuid: userData.regionGuid || "",
        superRegion: userData.superRegionName || userData.superRegion || "",
        superRegionGuid: userData.superRegionGuid || "",
        role: userData.roleName || userData.role || "",
        roleGuid: userData.roleGuid || "",
        countries: parsedCountries,
        status: userData.isActive ?? true,
      });
      // Set selected user for edit mode
      setSelectedUser({ displayName: email, mail: email });
      setEmailSearchText("");
    } else {
      setFormData({
        email: "",
        region: "",
        regionGuid: "",
        superRegion: "",
        superRegionGuid: "",
        role: "",
        roleGuid: "",
        countries: [],
        status: true,
      });
      setSelectedUser(null);
      setEmailSearchText("");
    }
  }, [userData, mode, isOpen]);

  // Auto-select regionGuid based on regionName when regions are loaded (for edit mode)
  useEffect(() => {
    if (
      mode === "edit" &&
      formData.region &&
      !formData.regionGuid &&
      regions.length > 0
    ) {
      const matchedRegion = regions.find(
        (r) => r.regionName?.toLowerCase() === formData.region?.toLowerCase(),
      );
      if (matchedRegion) {
        setFormData((prev) => ({
          ...prev,
          regionGuid: matchedRegion.regionGuid,
        }));
      }
    }
  }, [mode, formData.region, formData.regionGuid, regions]);

  // Auto-select roleGuid based on roleName when roles are loaded (for edit mode)
  useEffect(() => {
    if (
      mode === "edit" &&
      formData.role &&
      !formData.roleGuid &&
      roles.length > 0
    ) {
      const matchedRole = roles.find(
        (r) => r.roleName?.toLowerCase() === formData.role?.toLowerCase(),
      );
      if (matchedRole) {
        setFormData((prev) => ({
          ...prev,
          roleGuid: matchedRole.roleGuid,
        }));
      }
    }
  }, [mode, formData.role, formData.roleGuid, roles]);

  // Auto-select superRegionGuid based on superRegionName when superRegions are loaded (for edit mode)
  useEffect(() => {
    if (
      mode === "edit" &&
      formData.superRegion &&
      !formData.superRegionGuid &&
      superRegions.length > 0
    ) {
      const matchedSuperRegion = superRegions.find(
        (sr) =>
          sr.superRegionName?.toLowerCase() ===
          formData.superRegion?.toLowerCase(),
      );
      if (matchedSuperRegion) {
        setFormData((prev) => ({
          ...prev,
          superRegionGuid: matchedSuperRegion.superRegionGuid,
        }));
      }
    }
  }, [mode, formData.superRegion, formData.superRegionGuid, superRegions]);

  // Handle form field change
  const handleFormChange = (field, value) => {
    setSubmitError("");

    if (field === "region") {
      // Find the selected region by regionGuid
      const selectedRegion = regions.find((r) => r.regionGuid === value);

      setFormData((prev) => ({
        ...prev,
        region: selectedRegion?.regionName || value,
        regionGuid: value,
        superRegion: "",
        superRegionGuid: "",
        role: "",
        roleGuid: "",
        countries: [],
      }));
      setDuplicateWarning(""); // Clear warning when region changes
    } else if (field === "superRegion") {
      // Find the selected super region to get its guid
      const selectedSuperRegion = superRegions.find(
        (sr) => sr.superRegionGuid === value,
      );

      const newSuperRegionName = selectedSuperRegion?.superRegionName || value;

      setFormData((prev) => ({
        ...prev,
        superRegion: newSuperRegionName,
        superRegionGuid: value,
        countries: [], // Reset countries when super region changes
      }));

      // Check for duplicate on super region change (Quality Reviewer only;
      // Regional Lead is already restricted to a single region at role selection)
      if (
        mode === "add" &&
        isQualityReviewerRole() &&
        formData.email &&
        value
      ) {
        const candidate = {
          ...formData,
          superRegionGuid: value,
          superRegion: newSuperRegionName,
        };
        const duplicateExists = existingUsers.some(
          (user) =>
            user.emailId?.toLowerCase() === formData.email.toLowerCase() &&
            user.roleName?.toLowerCase() === formData.role?.toLowerCase() &&
            isSameSuperRegion(user, candidate),
        );
        if (duplicateExists) {
          setDuplicateWarning(
            "A record for this user already exists for the selected super region.",
          );
        } else {
          setDuplicateWarning("");
        }
      } else {
        setDuplicateWarning("");
      }
    } else if (field === "role") {
      // Find the selected role to get its guid
      const selectedRole = roles.find((r) => r.roleGuid === value);

      // If role is not Regional Lead, clear super region and countries
      const roleName = selectedRole?.roleName || "";
      const isRegionalLead =
        roleName.toLowerCase().includes("regional") &&
        roleName.toLowerCase().includes("lead");

      setFormData((prev) => ({
        ...prev,
        role: roleName,
        roleGuid: value,
        // Clear super region and countries if not Regional Lead
        ...(isRegionalLead
          ? {}
          : { superRegion: "", superRegionGuid: "", countries: [] }),
      }));

      const isQualityReviewer =
        roleName.toLowerCase().includes("quality") &&
        roleName.toLowerCase().includes("reviewer");

      const isQualitySpoc = roleName.toLowerCase().includes("quality spoc");

      // Check for duplicate on role change.
      // Quality SPOC: only one may be active system-wide.
      // Regional Lead: a user may lead only one region.
      // Quality Reviewer: validated by super region instead.
      // Other roles: Email + Area + Role.
      if (mode === "add" && isQualitySpoc) {
        const activeSpocExists = existingUsers.some(
          (user) =>
            user.roleName?.toLowerCase().includes("quality spoc") &&
            user.isActive !== false,
        );
        setDuplicateWarning(
          activeSpocExists ? "Quality SPOC already exists." : "",
        );
      } else if (
        mode === "add" &&
        !isRegionalLead &&
        !isQualityReviewer &&
        formData.email &&
        formData.region &&
        roleName
      ) {
        const duplicateExists = existingUsers.some(
          (user) =>
            user.emailId?.toLowerCase() === formData.email.toLowerCase() &&
            (user.regionGuid === formData.regionGuid ||
              user.regionName?.toLowerCase() ===
                formData.region?.toLowerCase()) &&
            user.roleName?.toLowerCase() === roleName.toLowerCase(),
        );
        if (duplicateExists) {
          setDuplicateWarning(
            "A record for this user already exists for the selected area and role.",
          );
        } else {
          setDuplicateWarning("");
        }
      } else {
        setDuplicateWarning("");
      }
    } else if (field === "countries") {
      setFormData((prev) => ({
        ...prev,
        countries: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const isQualitySpocRole = () =>
    formData.role?.toLowerCase().includes("quality spoc");

  // Handle form submit
  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");

    // Only one active Quality SPOC is allowed system-wide
    if (isQualitySpocRole()) {
      const isBeingActivated = mode === "add" || formData.status === true;
      if (isBeingActivated) {
        const spocExists = existingUsers.some(
          (user) =>
            user.roleName?.toLowerCase().includes("quality spoc") &&
            user.isActive !== false &&
            user.userGuId !== formData.userGuid &&
            user.userGuid !== formData.userGuid,
        );
        if (spocExists) {
          setSubmitError("Quality SPOC already exists.");
          setIsSubmitting(false);
          return;
        }
      }
    }

    // A user can be Regional Lead for only one region
    if (mode === "add" && isRegionalLeadRole() && formData.email) {
      const alreadyRegionalLead = existingUsers.some(
        (user) =>
          user.emailId?.toLowerCase() === formData.email.toLowerCase() &&
          user.roleName?.toLowerCase().includes("regional") &&
          user.roleName?.toLowerCase().includes("lead"),
      );

      
    }

    // Quality Reviewer may hold multiple entries; only same user + same super region is a duplicate
    if (
      mode === "add" &&
      isQualityReviewerRole() &&
      formData.email &&
      formData.superRegionGuid
    ) {
      const duplicateExists = existingUsers.some(
        (user) =>
          user.emailId?.toLowerCase() === formData.email.toLowerCase() &&
          user.roleName?.toLowerCase() === formData.role?.toLowerCase() &&
          isSameSuperRegion(user, formData),
      );

      if (duplicateExists) {
        setSubmitError(
          "A record for this user already exists for the selected super region. Please select a different super region or user.",
        );
        setIsSubmitting(false);
        return;
      }
    }

    // For add mode with non-Regional Lead role, check if user already exists for this area and role
    if (
      mode === "add" &&
      !isRegionalLeadRole() &&
      !isQualityReviewerRole() &&
      formData.email &&
      formData.region &&
      formData.role
    ) {
      const duplicateExists = existingUsers.some(
        (user) =>
          user.emailId?.toLowerCase() === formData.email.toLowerCase() &&
          (user.regionGuid === formData.regionGuid ||
            user.regionName?.toLowerCase() ===
              formData.region?.toLowerCase()) &&
          user.roleName?.toLowerCase() === formData.role?.toLowerCase(),
      );

      if (duplicateExists) {
        setSubmitError(
          "A record for this user already exists for the selected area and role. Please select a different area, role, or user.",
        );
        setIsSubmitting(false);
        return;
      }
    }

    // Build the API payload
    const payload = {
      userEmailId: formData.email,
      displayName: formData.displayName,
      roleGuid: formData.roleGuid,
      regionGuid: formData.regionGuid,
      superRegionGuid:
        formData.superRegionGuid || "00000000-0000-0000-0000-000000000000", // Placeholder if not set
      countryIds: formData.countries.map((c) =>
        typeof c.value === "number" ? c.value : parseInt(c.value, 10),
      ), // Ensure country IDs are integers
      status: formData.status,
    };

    // Include userGuid for edit mode
    if (mode === "edit" && formData.userGuid) {
      payload.userGuid = formData.userGuid;
    } else {
      payload.userGuid = "00000000-0000-0000-0000-000000000000"; // Placeholder for new user
    }

    try {
      await onSubmit(payload);
    } catch (error) {
      console.error("Error submitting user data:", error);
      // Get error message: ApiError.message (already contains API message), or fallback options
      const apiErrorMessage =
        error?.message ||
        error?.responseData?.message ||
        error?.responseData?.Message ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        "Failed to save user. Please try again.";
      setSubmitError(apiErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setFormData({
      email: "",
      displayName: "",
      region: "",
      regionGuid: "",
      superRegion: "",
      superRegionGuid: "",
      role: "",
      roleGuid: "",
      countries: [],
      status: true,
    });
    setSuperRegions([]);
    setRoles([]);
    setCountries([]);
    setSelectedUser(null);
    setEmailSearchText("");
    setUserSuggestions([]);
    setShowSuggestions(false);
    setSubmitError("");
    setDuplicateWarning("");
    onClose();
  };

  // Check if the selected role is Regional Lead
  const isRegionalLeadRole = () => {
    const roleName = formData.role?.toLowerCase() || "";
    return roleName.includes("regional") && roleName.includes("lead");
  };

  const isQualityReviewerRole = () => {
    const roleName = formData.role?.toLowerCase() || "";
    return roleName.includes("quality") && roleName.includes("reviewer");
  };

  // Check if form is valid (all fields filled)
  const isFormValid = () => {
    const baseValid =
      formData.email.trim() !== "" &&
      formData.regionGuid !== "" &&
      formData.roleGuid !== "";

    // If Regional Lead role, also require superRegionGuid and at least one country
    if (isRegionalLeadRole() || isQualityReviewerRole()) {
      return (
        baseValid &&
        formData.superRegionGuid !== "" &&
        formData.countries.length > 0
      );
    }

    return baseValid;
  };

  if (!isOpen) return null;

  return (
    <div className="user-modal-overlay">
      <div className="user-modal">
        <div className="user-modal-header">
          <h2 className="user-modal-title">
            {mode === "add" ? "Add User" : "Edit User"}
          </h2>
          <button className="user-modal-close" onClick={handleClose}>
            <CloseIcon width={20} height={20} color="#dedee2" />
          </button>
        </div>

        <div className="user-modal-content">
          {/* Disclaimer */}
          <div className="user-modal-disclaimer">
            <InfoCircleIcon width={16} height={16} color="#DEDEE2" />
            <span>
              When granting access to any team member, regardless of role,
              ensure they comply with all applicable relevant local independence
              requirements before accessing this solution or its AI-enabled
              capabilities
            </span>
          </div>

          {/* Row 1: Email ID and Role */}
          <div className="user-form-row">
            <div className="user-form-group">
              <label className="user-form-label">Email ID</label>
              <div
                className="user-form-input-wrapper user-email-autocomplete"
                ref={suggestionRef}
              >
                {selectedUser ? (
                  // Show selected user
                  <div className="user-selected-email">
                    <div className="user-selected-info">
                      {selectedUser.photo ? (
                        <img
                          src={selectedUser.photo}
                          alt={selectedUser.displayName}
                          className="user-selected-photo"
                        />
                      ) : (
                        <div className="user-selected-photo-placeholder">
                          {(
                            selectedUser.displayName ||
                            selectedUser.mail ||
                            "U"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                      <div className="user-selected-details">
                        <span className="user-selected-name">
                          {selectedUser.displayName}
                        </span>
                        <span className="user-selected-mail">
                          {selectedUser.mail}
                        </span>
                      </div>
                    </div>
                    {mode !== "edit" && (
                      <button
                        type="button"
                        className="input-clear-button"
                        onClick={handleClearEmail}
                      >
                        <CloseIcon width={16} height={16} color="#737387" />
                      </button>
                    )}
                  </div>
                ) : (
                  // Show search input
                  <>
                    <input
                      type="text"
                      className={`user-form-input${hasEmptySearchResults ? " user-form-input--error" : ""}`}
                      value={emailSearchText}
                      onChange={handleEmailSearchChange}
                      placeholder="Search by name or email..."
                      autoComplete="off"
                      disabled={mode === "edit"}
                    />
                    {isLoadingSuggestions && (
                      <span className="email-search-loading">Searching...</span>
                    )}
                    {hasEmptySearchResults && (
                      // <span className="email-search-error-message">
                      //   {USER_MANAGEMENT.TOOTLTIP}
                      // </span>
                      <div className="error-field-tooltip">
                        <CustomTooltipBottom
                          trigger={
                            <InfoCircleIcon
                              width="14"
                              height="14"
                              color="#ea011d"
                            />
                          }
                          normalText={USER_MANAGEMENT.TOOTLTIP}
                          minHeight="2rem"
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Suggestions dropdown */}
                {showSuggestions && userSuggestions.length > 0 && (
                  <ul className="user-suggestions-list">
                    {userSuggestions.map((user) => (
                      <li
                        key={user.id}
                        className="user-suggestion-item"
                        onClick={() => handleSelectUser(user)}
                      >
                        {user.photo ? (
                          <img
                            src={user.photo}
                            alt={user.displayName}
                            className="user-suggestion-photo"
                          />
                        ) : (
                          <div className="user-suggestion-avatar">
                            {(user.displayName || user.mail || "U")
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                        )}
                        <div className="user-suggestion-info">
                          <span className="user-suggestion-name">
                            {user.displayName}
                          </span>
                          <span className="user-suggestion-email">
                            {user.mail}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="user-form-group">
              <label className="user-form-label">Area</label>
              <div className="user-form-select-wrapper">
                <MotifFormField>
                  <MotifSelect
                    className="user-form-select"
                    value={formData.regionGuid}
                    onChange={(value) => handleFormChange("region", value)}
                    disabled={isLoadingRegions || mode === "edit"}
                    placeholder={
                      isLoadingRegions ? "Loading..." : "Select Area"
                    }
                    filter
                  >
                    {regions.map((region) => (
                      <MotifOption
                        key={region.regionGuid}
                        value={region.regionGuid}
                      >
                        {region.regionName || region.name || region}
                      </MotifOption>
                    ))}
                  </MotifSelect>
                </MotifFormField>
              </div>
            </div>
          </div>

          {/* Row 2: Area and Super Region */}
          <div className="user-form-row">
            <div className="user-form-group">
              <label className="user-form-label">Role</label>
              <div className="user-form-select-wrapper">
                <MotifFormField>
                  <MotifSelect
                    className="user-form-select"
                    value={formData.roleGuid}
                    onChange={(value) => handleFormChange("role", value)}
                    disabled={isLoadingRoles || !formData.regionGuid}
                    filter
                    placeholder={
                      isLoadingRoles
                        ? "Loading..."
                        : !formData.region
                          ? "Select Area first"
                          : "Select Role"
                    }
                  >
                    {roles.map((role) => (
                      <MotifOption key={role.roleGuid} value={role.roleGuid}>
                        {role.roleName || role.name || role}
                      </MotifOption>
                    ))}
                  </MotifSelect>
                </MotifFormField>
              </div>
            </div>
            {/* Super Region - only shown when role is Regional Lead */}
            {(isRegionalLeadRole() || isQualityReviewerRole()) && (
              <div className="user-form-group">
                <label className="user-form-label">Super Region</label>
                <div className="user-form-select-wrapper">
                  <MotifFormField>
                    <MotifSelect
                      className="user-form-select"
                      value={formData.superRegionGuid}
                      onChange={(value) =>
                        handleFormChange("superRegion", value)
                      }
                      disabled={isLoadingSuperRegions || !formData.regionGuid}
                      filter
                      placeholder={
                        isLoadingSuperRegions
                          ? "Loading..."
                          : "Select Super Region"
                      }
                    >
                      {superRegions.map((superRegion) => (
                        <MotifOption
                          key={superRegion.superRegionGuid}
                          value={superRegion.superRegionGuid}
                        >
                          {superRegion.superRegionName ||
                            superRegion.name ||
                            superRegion}
                        </MotifOption>
                      ))}
                    </MotifSelect>
                  </MotifFormField>
                </div>
              </div>
            )}
          </div>

          {/* Row 3: Region/Country multi-select - only shown when role is Regional Lead */}
          {(isRegionalLeadRole() || isQualityReviewerRole()) && (
            <div className="user-form-group user-form-group-full">
              <div className="user-form-label-with-info">
                <label className="user-form-label">Region/Country</label>
              </div>
              <div className="user-form-multiselect-wrapper">
                <MultiSelect
                  options={countries.map((country, index) => ({
                    label:
                      country.countryName || country.name || String(country),
                    value: country.countryId,
                  }))}
                  value={formData.countries}
                  onChange={(selected) =>
                    handleFormChange("countries", selected)
                  }
                  labelledBy="Select countries"
                  isLoading={isLoadingCountries}
                  disabled={!formData.superRegionGuid || isLoadingCountries}
                  hasSelectAll={true}
                  disableSearch={false}
                  overrideStrings={{
                    selectSomeItems: formData.superRegionGuid
                      ? "Select countries..."
                      : "Select Super Region first",
                    allItemsAreSelected: "All countries selected",
                    selectAll: "Select All",
                    search: "Search",
                    noOptions: "No countries available",
                  }}
                  className="user-country-multiselect"
                  multiple
                />
              </div>
            </div>
          )}

          {/* Status dropdown - only shown in edit mode */}
          {mode === "edit" && (
            <div className="user-form-group">
              <label className="user-form-label">Status</label>
              <div className="user-form-select-wrapper">
                <MotifFormField>
                  <MotifSelect
                    className="user-form-select"
                    value={formData.status ? "Active" : "Inactive"}
                    onChange={(value) =>
                      handleFormChange("status", value === "Active")
                    }
                    filter
                  >
                    <MotifOption value="Active">Active</MotifOption>
                    <MotifOption value="Inactive">Inactive</MotifOption>
                  </MotifSelect>
                </MotifFormField>
              </div>
            </div>
          )}
        </div>

        {duplicateWarning && (
          <div className="user-modal-warning">{duplicateWarning}</div>
        )}

        {submitError && <div className="user-modal-error">{submitError}</div>}

        <div className="user-modal-footer">
          <button className="user-modal-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="user-modal-submit"
            onClick={handleFormSubmit}
            disabled={!isFormValid() || isSubmitting || duplicateWarning}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
